import { AvatarFallback, AvatarImage } from '@radix-ui/react-avatar';
import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';

import {
  ScrollableCellWithAvatar,
  ScrollableCellWithColorDot,
} from '@/components/data-table/cells';
import { Avatar } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import i18n from '@/core/i18n';
import { capitalizeFirst, cn } from '@/lib/utils';
import { StatusBadge, StatusBadgeType } from '@/modules/common';
import { getServiceIcon } from '@/modules/onboarding/integrations-setup/helpers/data';
import { ScrollableText } from '@/modules/planner/components/assignment/text/scrollable-text';
import { IndustriesCell } from '@/modules/projects/components/project-details/table-cells/industries-cell';
import { ManagersCell } from '@/modules/projects/components/project-details/table-cells/managers-cell';
import {
  ProjectBillingModel,
  ProjectListItem,
  ProjectStatus,
} from '@/modules/projects/types/project';
import { formatDisplayDate } from '@/utils/date-formatters';

const columnHelper = createColumnHelper<ProjectListItem>();

const renderArchivedText = (status: ProjectStatus, value?: string) => {
  const noValue = i18n.t('common:table.noValue');
  const displayValue = value || noValue;

  return (
    <ScrollableText
      className={cn('whitespace-nowrap', status === ProjectStatus.Archived && 'opacity-50')}
    >
      {displayValue}
    </ScrollableText>
  );
};

export const getColumns = () => [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => (
      <label className="flex cursor-pointer items-center justify-center px-3 py-3">
        <Checkbox
          checked={
            table.getIsAllRowsSelected()
              ? true
              : table.getIsSomeRowsSelected()
                ? 'indeterminate'
                : false
          }
          onCheckedChange={value => table.toggleAllRowsSelected(!!value)}
          aria-label={i18n.t('employeesTable.selectAll')}
        />
      </label>
    ),
    cell: ({ row }) => (
      <label className="flex cursor-pointer items-center justify-center px-3 py-4">
        <Checkbox
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onCheckedChange={value => row.toggleSelected(!!value)}
          aria-label={i18n.t('employeesTable.selectRow')}
        />
      </label>
    ),
    enableSorting: false,
    enableColumnFilter: false,
    size: 40,
    minSize: 40,
    maxSize: 40,
    enablePinning: true,
  }),
  columnHelper.accessor('name', {
    size: 200,
    minSize: 150,
    header: i18n.t('authenticated.projects:headers.projectName'),
    cell: ({ getValue, row }) => {
      const isArchived = row.original.status === ProjectStatus.Archived;
      return (
        <ScrollableCellWithColorDot
          text={getValue()}
          image={row.original.logoUrl}
          avatarName={getValue()}
          avatarSize="sm"
          alt={`${getValue()} logo`}
          dotColor={row.original.colorHex}
          className={cn(isArchived && 'opacity-50')}
        />
      );
    },
    enablePinning: true,
    enableHiding: false,
  }),
  columnHelper.accessor('status', {
    header: i18n.t('authenticated.projects:common.status'),
    size: 120,
    minSize: 100,
    enableHiding: false,
    cell: ({ getValue, row }) => {
      const status = getValue() || ProjectStatus.Active;
      const project = row.original;

      let warningTooltip: string | undefined;
      let infoTooltip: string | undefined;

      if (project.nextArchiveDate) {
        const formattedDate = formatDisplayDate(project.nextArchiveDate);
        warningTooltip = i18n.t('authenticated.projects:scheduledArchive.willBeArchivedOn', {
          date: formattedDate,
        });
      } else if (project.nextUnarchiveDate) {
        const formattedDate = formatDisplayDate(project.nextUnarchiveDate);
        warningTooltip = i18n.t('authenticated.projects:scheduledArchive.willBeUnarchivedOn', {
          date: formattedDate,
        });
      }

      if (
        status === ProjectStatus.Tentative &&
        project.tentativeProbability !== null &&
        project.tentativeProbability !== undefined
      ) {
        infoTooltip = i18n.t('authenticated.projects:table.probabilityTooltip', {
          probability: project.tentativeProbability,
        });
      }

      return (
        <StatusBadge
          type={StatusBadgeType.Project}
          status={status}
          label={capitalizeFirst(status)}
          warningTooltip={warningTooltip}
          infoTooltip={infoTooltip}
        />
      );
    },
  }),
  columnHelper.accessor(row => row.client?.name, {
    id: 'client.name',
    header: i18n.t('authenticated.projects:headers.client'),
    size: 200,
    minSize: 150,
    enableHiding: false,
    cell: ({ getValue, row }) => {
      const clientName = getValue();
      const isArchived = row.original.status === ProjectStatus.Archived;
      return clientName ? (
        <ScrollableCellWithAvatar
          text={clientName}
          image={row.original.client?.logoUrl}
          avatarName={clientName}
          avatarSize="sm"
          alt={`${clientName} logo`}
          className={cn(isArchived && 'opacity-50')}
        />
      ) : (
        renderArchivedText(row.original.status)
      );
    },
  }),
  columnHelper.accessor('managers', {
    id: 'manager.name',
    header: i18n.t('authenticated.projects:filters.manager'),
    size: 200,
    minSize: 150,
    cell: ({ getValue, row }) => {
      const managers = getValue() || [];
      const isArchived = row.original.status === ProjectStatus.Archived;

      if (managers.length === 0) {
        return renderArchivedText(row.original.status);
      }

      return <ManagersCell managers={managers} isArchived={isArchived} />;
    },
  }),
  columnHelper.accessor('billingModel', {
    header: i18n.t('authenticated.projects:filters.billingModel'),
    size: 150,
    minSize: 120,
    cell: ({ getValue, row }) => {
      const billingModel = getValue() as ProjectBillingModel;
      if (!billingModel) return renderArchivedText(row.original.status);

      // Map billing model enum values to translation keys
      const billingModelKey = {
        [ProjectBillingModel.TM]: 'timeMaterial',
        [ProjectBillingModel.NonBillable]: 'nonBillable',
        [ProjectBillingModel.FixedPrice]: 'fixedPrice',
      }[billingModel];

      const displayValue = billingModelKey
        ? i18n.t(`authenticated.projects:filters.${billingModelKey}`)
        : billingModel;

      return renderArchivedText(row.original.status, displayValue);
    },
  }),
  columnHelper.accessor('defaultCurrency.code', {
    header: i18n.t('authenticated.projects:headers.currency'),
    size: 100,
    minSize: 80,
    cell: ({ getValue, row }) => {
      const currencyCode = getValue();
      return renderArchivedText(row.original.status, currencyCode || undefined);
    },
  }),
  columnHelper.accessor('projectStartDate', {
    header: i18n.t('authenticated.projects:headers.startDate'),
    size: 130,
    minSize: 110,
    cell: ({ getValue, row }) => {
      const dateString = getValue();
      if (!dateString) return renderArchivedText(row.original.status);
      try {
        const date = new Date(dateString);
        const formattedDate = format(date, 'd MMM, yyyy');
        return renderArchivedText(row.original.status, formattedDate);
      } catch {
        return renderArchivedText(row.original.status, dateString);
      }
    },
  }),
  /*For now end date in projects table must not be show. In the future we may revert this change.
  TODO: If it's not reverted till 01.03.2027 - delete this part of code. */
  /*columnHelper.accessor('projectEndDate', {
    header: i18n.t('authenticated.projects:headers.endDate'),
    cell: ({ getValue, row }) => {
      const dateString = getValue();
      if (!dateString) return renderArchivedText(row.original.status);
      try {
        const date = new Date(dateString);
        const formattedDate = format(date, 'd MMM, yyyy');
        return renderArchivedText(row.original.status, formattedDate);
      } catch {
        return renderArchivedText(row.original.status, dateString);
      }
    },
  }),*/
  columnHelper.accessor('industries', {
    header: i18n.t('authenticated.projects:headers.industries'),
    size: 200,
    minSize: 150,
    cell: ({ getValue, row }) => {
      const industries = getValue() || [];
      const isArchived = row.original.status === ProjectStatus.Archived;

      if (industries.length === 0) {
        return renderArchivedText(row.original.status);
      }

      return <IndustriesCell industries={industries} isArchived={isArchived} />;
    },
  }),
  columnHelper.accessor('connectedIntegrations', {
    header: i18n.t('authenticated.projects:headers.integrations'),
    size: 150,
    minSize: 120,
    cell: ({ getValue, row }) => {
      const integrations = getValue() || [];
      const isArchived = row.original.status === ProjectStatus.Archived;

      if (integrations.length === 0) {
        return renderArchivedText(
          row.original.status,
          i18n.t('authenticated.projects:table.noConnections')
        );
      }

      return (
        <div
          className={cn(
            'flex -space-x-2 *:data-[slot=avatar]:ring-2 *:data-[slot=avatar]:ring-background *:data-[slot=avatar]:grayscale',
            isArchived && 'opacity-50'
          )}
        >
          {integrations.map(integration => {
            return (
              <SimpleTooltip key={integration} content={integration}>
                <Avatar slot="avatar" className="flex h-6 w-6 items-center justify-center bg-muted">
                  <AvatarImage
                    src={getServiceIcon(integration)}
                    alt={integration}
                    className="h-4 w-4"
                  />
                  <AvatarFallback>{integration}</AvatarFallback>
                </Avatar>
              </SimpleTooltip>
            );
          })}
        </div>
      );
    },
  }),
];
